import type { RegisterSection, SectionId } from '../model/operations';

export type ScrollAdapter = {
  registerSection: RegisterSection;
  scrollToSection: (sectionId: SectionId) => void;
  observeSections: (
    sectionIds: SectionId[],
    onActiveChange: (sectionId: SectionId) => void
  ) => () => void;
  dispose: () => void;
};

const SCROLL_SETTLE_MS = 150;
const SCROLL_FALLBACK_MS = 600;

function findScrollContainer(element: Element): Element | null {
  let parent = element.parentElement;

  while (parent && parent !== document.body) {
    const { overflow, overflowY } = window.getComputedStyle(parent);

    const isScrollable = /auto|scroll/.test(overflow + overflowY);

    if (isScrollable && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null; // null = browser viewport
}

export function createScrollAdapter(): ScrollAdapter {
  const elementsById = new Map<SectionId, HTMLElement>();
  const idsByElement = new Map<Element, SectionId>();
  const unsubscribers = new Set<() => void>();

  let isScrolling = false;
  let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;
  let scrollEndListener: (() => void) | null = null;

  function releaseScrollGuard() {
    isScrolling = false;

    if (scrollEndTimer !== null) {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = null;
    }

    if (scrollEndListener) {
      window.removeEventListener('scroll', scrollEndListener);
      scrollEndListener = null;
    }
  }

  const registerSection: RegisterSection = (sectionId, element) => {
    const previous = elementsById.get(sectionId);

    if (previous) {
      idsByElement.delete(previous);
      elementsById.delete(sectionId);
    }

    if (element) {
      elementsById.set(sectionId, element);
      idsByElement.set(element, sectionId);
    }
  };

  function scrollToSection(sectionId: SectionId) {
    const element = elementsById.get(sectionId);

    releaseScrollGuard();

    if (!element) {
      return;
    }

    isScrolling = true;

    const handleScrollEnd = () => {
      if (scrollEndTimer !== null) {
        clearTimeout(scrollEndTimer);
      }

      scrollEndTimer = setTimeout(releaseScrollGuard, SCROLL_SETTLE_MS);
    };

    scrollEndTimer = setTimeout(releaseScrollGuard, SCROLL_FALLBACK_MS);

    // Listens on window even if the observer root is a non-window container,
    // matching legacy; the fallback timer releases the guard when none fires.
    scrollEndListener = handleScrollEnd;
    window.addEventListener('scroll', handleScrollEnd, { passive: true });

    // scrollIntoView works regardless of which ancestor is the scroll container
    // (browser viewport or an overflow element), avoiding the need to resolve
    // the scroll root manually.
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function observeSections(
    sectionIds: SectionId[],
    onActiveChange: (sectionId: SectionId) => void
  ) {
    const elements = sectionIds
      .map(sectionId => elementsById.get(sectionId))
      .filter((element): element is HTMLElement => element !== undefined);

    if (elements.length === 0) {
      return () => undefined;
    }

    // Resolve the actual scroll container at observer-setup time so rootMargin
    // is applied against the element that is really scrolling. If no overflow
    // ancestor is found we fall back to null (browser viewport).
    const scrollContainer = findScrollContainer(elements[0]);

    // Tracks which section IDs are currently inside the active zone.
    // Maintained across IO callbacks so we always have the full visible set,
    // not just the entries that changed in the current batch. It is updated
    // even while a click-scroll is in flight, otherwise a section that left the
    // zone mid-scroll would stay in the set (deliberate deviation from legacy).
    const visibleIds = new Set<SectionId>();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const sectionId = idsByElement.get(entry.target);

          if (sectionId === undefined) {
            return;
          }

          if (entry.isIntersecting) {
            visibleIds.add(sectionId);
          } else {
            visibleIds.delete(sectionId);
          }
        });

        if (isScrolling) {
          return;
        }

        const active = sectionIds.find(sectionId => visibleIds.has(sectionId));

        if (active !== undefined) {
          onActiveChange(active);
        }
      },
      {
        root: scrollContainer,
        rootMargin: '-68px 0px -55% 0px', // offset for fixed $header-height (4.25rem = 68px)
        // threshold: 0 fires only when a section enters or exits the active zone,
        // not on every incremental ratio change — eliminates the highlight flicker.
        threshold: 0,
      }
    );

    elements.forEach(element => observer.observe(element));

    const unsubscribe = () => {
      observer.disconnect();
      visibleIds.clear();
      unsubscribers.delete(unsubscribe);
    };

    unsubscribers.add(unsubscribe);

    return unsubscribe;
  }

  function dispose() {
    releaseScrollGuard();
    [...unsubscribers].forEach(unsubscribe => unsubscribe());
  }

  return { registerSection, scrollToSection, observeSections, dispose };
}
